import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { db } from "../App";
import { collection, getDocs, orderBy, query, where } from "firebase/firestore";
import TagPill from "../components/TagPill";

function safeStr(x) {
  return (x || "").toString();
}

function Projects() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [tagFilter, setTagFilter] = useState("all");

  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setErr("");

      try {
        const qy = query(collection(db, "publicProjects"), where("published", "==", true), orderBy("sortOrder", "asc"));
        const snap = await getDocs(qy);
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (mounted) setItems(rows);
      } catch (e) {
        console.error("Projects load error:", e);
        if (mounted) setErr(e?.message || "Failed to load projects.");
      }

      if (mounted) setLoading(false);
    }

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const allTags = useMemo(() => {
    const s = new Set();
    items.forEach((it) => (it.tags || []).forEach((t) => s.add(safeStr(t).trim())));
    return Array.from(s).filter(Boolean).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const filtered = useMemo(() => {
    if (tagFilter === "all") return items;
    return items.filter((it) => (it.tags || []).map((t) => safeStr(t)).includes(tagFilter));
  }, [items, tagFilter]);

  return (
    <div className="container mx-auto p-4 space-y-5">
      <div className="space-y-1">
        <h1 className="text-3xl font-semibold">Projects</h1>
        <p className="opacity-80">
          Case studies — the story, the constraints, the decisions, and the result.
        </p>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3 border rounded p-3 bg-white">
        <div className="text-sm font-semibold opacity-80">Filter by tag</div>
        <select className="border p-2 rounded w-full md:w-[280px]" value={tagFilter} onChange={(e) => setTagFilter(e.target.value)}>
          <option value="all">All</option>
          {allTags.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <div className="md:ml-auto text-sm opacity-70">
          Showing <span className="font-semibold">{filtered.length}</span>
        </div>
      </div>

      {loading && <div className="opacity-70">Loading…</div>}
      {err && <div className="text-red-600">{err}</div>}

      {!loading && !err && filtered.length === 0 && <div className="opacity-70">No published projects yet.</div>}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {filtered.map((it) => {
          const slug = safeStr(it.slug);
          const title = safeStr(it.title) || "Untitled";
          const summary = safeStr(it.summary);
          const cover = safeStr(it.coverImageUrl);

          return (
            <Link
              key={it.id}
              to={`/projects/${encodeURIComponent(slug)}`}
              className="border rounded overflow-hidden bg-white hover:shadow-sm transition"
            >
              <div className="bg-secondary">
                {cover ? (
                  <img src={cover} alt={title} className="w-full h-48 object-cover" />
                ) : (
                  <div className="w-full h-48 flex items-center justify-center opacity-60">No cover image</div>
                )}
              </div>

              <div className="p-4 space-y-2">
                <div className="text-lg font-semibold">{title}</div>
                {summary && <div className="text-sm opacity-80">{summary}</div>}

                <div className="flex flex-wrap gap-2 pt-2">
                  {(it.tags || []).slice(0, 4).map((t, idx) => (
                    <TagPill key={`${t}-${idx}`}>{safeStr(t)}</TagPill>
                  ))}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export default Projects;
